/**
 * Batch A — stale asset / lazy-chunk recovery (#5).
 * Detects dynamic-import failures (deploy/SW precache skew) and forces one
 * controlled reload after asking the waiting SW to skipWaiting.
 */

const RECOVERY_KEY = "pwa-chunk-recovery";
const RECOVERY_TS_KEY = "pwa-chunk-recovery-ts";

export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /Failed to fetch dynamically imported module|Loading chunk [\d]+ failed|ChunkLoadError|error loading dynamically imported module/i.test(
    msg
  );
}

export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err ?? "Ismeretlen hiba");
}

/** True if we already attempted recovery recently (avoid reload loops). */
export function alreadyAttemptedChunkRecovery(windowMs = 20_000): boolean {
  try {
    if (sessionStorage.getItem(RECOVERY_KEY) === "1") {
      const ts = Number(sessionStorage.getItem(RECOVERY_TS_KEY) || "0");
      if (Date.now() - ts < windowMs) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

export function markChunkRecoveryAttempt(): void {
  try {
    sessionStorage.setItem(RECOVERY_KEY, "1");
    sessionStorage.setItem(RECOVERY_TS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearChunkRecoveryMark(): void {
  try {
    sessionStorage.removeItem(RECOVERY_KEY);
    sessionStorage.removeItem(RECOVERY_TS_KEY);
  } catch {
    /* ignore */
  }
}

/** Ask waiting SW to activate, then hard-reload once. */
export async function recoverFromStaleAssets(): Promise<void> {
  markChunkRecoveryAttempt();
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
      await new Promise((r) => setTimeout(r, 300));
    } else if (reg?.installing) {
      await new Promise<void>((resolve) => {
        const sw = reg.installing!;
        const onChange = () => {
          if (sw.state === "installed" || sw.state === "activated") {
            sw.removeEventListener("statechange", onChange);
            resolve();
          }
        };
        sw.addEventListener("statechange", onChange);
        setTimeout(() => resolve(), 2000);
      });
      reg.waiting?.postMessage({ type: "SKIP_WAITING" });
    }
  } catch {
    /* ignore — still reload */
  }
  window.location.reload();
}

/** Install global listeners once (main.tsx). */
export function installChunkLoadRecoveryListeners(): void {
  if (typeof window === "undefined") return;
  const w = window as Window & { __pwaChunkRecoveryInstalled?: boolean };
  if (w.__pwaChunkRecoveryInstalled) return;
  w.__pwaChunkRecoveryInstalled = true;

  // Successful boot after recovery — clear the flag so future deploys can recover again
  if (sessionStorage.getItem(RECOVERY_KEY) === "1") {
    // Clear after a short delay so ErrorBoundary can still read "already attempted" if boot fails again
    window.setTimeout(() => clearChunkRecoveryMark(), 5000);
  }

  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadError(event.reason)) return;
    if (alreadyAttemptedChunkRecovery()) return;
    event.preventDefault();
    void recoverFromStaleAssets();
  });
}
